import { type ReactNode, createContext, useContext, useMemo } from 'react';

import type { MicrophonePermissionOutcome } from '@repo/videa-notes';

import type { VoiceNoteTemplateOption } from '../types';

/**
 * Recording context value interface.
 * Contains state and handlers for audio capture functionality.
 */
export interface VideaNotesRecordingContextValue {
  // Recorder state
  recorderStatus: string;
  mediaBlobUrl: string | undefined;

  // Template selection
  templateOptions: VoiceNoteTemplateOption[];
  selectedTemplate: string | undefined;
  selectedTemplateName: string | null;
  selectedTemplateUserInstructions: string | null;
  onSelectedTemplate: (templateId: string | undefined) => void;

  // Language selection
  supportedLanguages: string[];
  selectedLanguage: string;
  onSelectedLanguage: (language: string) => void;

  // Recording feature flags
  isAudioUploadEnabled: boolean;
  showDevTools: boolean;

  // Recording handlers
  onMonitoringStreamChange: (stream: MediaStream | null) => void;
  onCancelRecording: () => void;
  onCancelRecordingConfirmation: () => void;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
  onStartRecording: () => void;
  onStartProviderOnlyRecording: () => void;
  onGenerateNote: (uploadedFile?: File, blobUrl?: string) => void;
  onPatientConsentDenied: () => void;
  /** Called after each microphone permission request with outcome (for analytics) */
  onPermissionOutcome?: (outcome: MicrophonePermissionOutcome) => void;
}

const VideaNotesRecordingContext =
  createContext<VideaNotesRecordingContextValue | null>(null);

interface VideaNotesRecordingProviderProps {
  value: VideaNotesRecordingContextValue;
  children: ReactNode;
}

/**
 * Provider component for VideaNotes recording context.
 * Contains state and handlers for audio capture functionality.
 */
export const VideaNotesRecordingProvider = ({
  value,
  children,
}: VideaNotesRecordingProviderProps) => {
  return (
    <VideaNotesRecordingContext.Provider value={value}>
      {children}
    </VideaNotesRecordingContext.Provider>
  );
};

/**
 * Hook to access VideaNotes recording context.
 * Must be used within a VideaNotesRecordingProvider.
 */
export const useVideaNotesRecordingContext =
  (): VideaNotesRecordingContextValue => {
    const context = useContext(VideaNotesRecordingContext);
    if (!context) {
      throw new Error(
        'useVideaNotesRecordingContext must be used within a VideaNotesRecordingProvider',
      );
    }
    return context;
  };

/**
 * Hook to access recorder state from recording context.
 * Memoized to prevent unnecessary re-renders.
 */
export const useRecorderState = () => {
  const { recorderStatus, mediaBlobUrl } = useVideaNotesRecordingContext();
  return useMemo(
    () => ({ recorderStatus, mediaBlobUrl }),
    [recorderStatus, mediaBlobUrl],
  );
};

/**
 * Hook to access prompt/template selection and feature flags from recording context.
 * Memoized to prevent unnecessary re-renders.
 */
export const useTemplateSelection = () => {
  const {
    templateOptions,
    selectedTemplate,
    selectedTemplateName,
    selectedTemplateUserInstructions,
    onSelectedTemplate,
    supportedLanguages,
    selectedLanguage,
    onSelectedLanguage,
    isAudioUploadEnabled,
    showDevTools,
  } = useVideaNotesRecordingContext();
  return useMemo(
    () => ({
      templateOptions,
      selectedTemplate,
      selectedTemplateName,
      selectedTemplateUserInstructions,
      onSelectedTemplate,
      supportedLanguages,
      selectedLanguage,
      onSelectedLanguage,
      isAudioUploadEnabled,
      showDevTools,
    }),
    [
      templateOptions,
      selectedTemplate,
      selectedTemplateName,
      selectedTemplateUserInstructions,
      onSelectedTemplate,
      supportedLanguages,
      selectedLanguage,
      onSelectedLanguage,
      isAudioUploadEnabled,
      showDevTools,
    ],
  );
};

/**
 * Hook to access recording handlers from recording context.
 * Memoized to prevent unnecessary re-renders.
 */
export const useRecordingHandlers = () => {
  const {
    onMonitoringStreamChange,
    onCancelRecording,
    onCancelRecordingConfirmation,
    onPauseRecording,
    onResumeRecording,
    onStartRecording,
    onStartProviderOnlyRecording,
    onGenerateNote,
    onPatientConsentDenied,
    onPermissionOutcome,
  } = useVideaNotesRecordingContext();
  return useMemo(
    () => ({
      onMonitoringStreamChange,
      onCancelRecording,
      onCancelRecordingConfirmation,
      onPauseRecording,
      onResumeRecording,
      onStartRecording,
      onStartProviderOnlyRecording,
      onGenerateNote,
      onPatientConsentDenied,
      onPermissionOutcome,
    }),
    [
      onMonitoringStreamChange,
      onCancelRecording,
      onCancelRecordingConfirmation,
      onPauseRecording,
      onResumeRecording,
      onStartRecording,
      onStartProviderOnlyRecording,
      onGenerateNote,
      onPatientConsentDenied,
      onPermissionOutcome,
    ],
  );
};
